<template>
  <div class="space-y-12">
    <div>
      <THeading
        :level="2"
        tag="h2"
        class="mb-4 justify-center"
      >
        {{ t('heading', { rvTitle: locale === 'fr' ? rvTitleArticle : rvTitle, locationTitle: locale === 'fr' ? locationTitle : locationName }) }}
      </THeading>

      <p class="text-center text-lg font-medium">
        {{ content }}
      </p>
    </div>

    <div class="grid gap-4 grid-cols-1 lg:grid-cols-3">
      <div
        v-for="(item, index) in (tm('steps') as Step[])"
        :key="item.title"
        class="flex flex-col gap-2"
      >
        <TImage
          :src="getStaticImage({ path: `/web/images/custom-icons/discover-delivery/slide${index}.svg` })"
          alt=""
          height="160"
          width="240"
          class="aspect-video mx-auto"
        />

        <THeading
          :level="5"
          tag="h3"
        >
          {{ rt(item.title) }}
        </THeading>

        <I18nT
          :keypath="`steps.${index}.text`"
          tag="p"
        >
          <template #locationTitle>
            {{ locationTitle }}
          </template>
        </I18nT>
      </div>
    </div>

    <div class="mx-auto text-center">
      <TButton
        variant="highlight"
        @click="emit('show-more')"
      >
        {{ t('exploreRentalRVs', { rvTitle, locationTitle }) }}
      </TButton>
    </div>
  </div>
</template>

<script setup lang="ts">
type Step = {
  title: string
  text: string
}

const props = defineProps<{
  keyword?: string
  locationTitle?: string
  locationTitleShort?: string
  locationName?: string
  locationNearby?: string
  keepCurrentSeoContent?: boolean
  rvCount?: number
  minPrice?: number
  campgroundCount?: number
}>()

const emit = defineEmits<{
  'show-more': []
}>()

const { locale, t, tm, rt } = useI18n()
const { getStaticImage } = useImage()
const { priceDisplay } = usePrice()

const { getKeywordTitle } = usePointOfInterest()

const rvTitle = computed(() => getKeywordTitle(props.keyword, { casing: 'sentence' }))
const rvTitleArticle = computed(() => getKeywordTitle(props.keyword, { prefix: 'article' }))

const content = computed(() => {
  if (props.keepCurrentSeoContent) {
    return t('content', { locationTitle: props.locationTitle })
  }

  return t('destination', {
    rvCountLabel: t('destinationRvCount', { n: props.rvCount ?? 0 }),
    locationTitleShort: props.locationTitleShort,
    minPrice: priceDisplay({ value: props.minPrice, round: true }),
    campgroundDelivery: t('campgroundDelivery', props.campgroundCount ?? 0),
    locationNearby: props.locationNearby,
  })
})
</script>

<i18n lang="json">
  {
    "en": {
      "heading": "{locationTitle} {rvTitle} rental: How it works",
      "content": "Planning an RV trip {locationTitle} is simple with RVezy. Search available RVs, compare options, choose pickup or delivery, and book securely online. Whether you are heading to a campground, national park, or weekend getaway, your adventure starts here.",
      "campgroundDelivery": "a campground | a campground | any of {n} campgrounds",
      "destinationRvCount": "RVs for rent | 1 RV for rent | {n} RVs for rent",
      "destination": "{rvCountLabel} {locationTitleShort}, from {minPrice} a night. Pick one up yourself, or have the owner deliver it and set it up at {campgroundDelivery} {locationNearby}. Travel trailers, motorhomes and camper vans, all insured, most pet-friendly.",
      "steps": [
        {
          "title": "1. Explore RV rentals",
          "text": "Browse a wide range of campervans, travel trailers, and motorhomes. Filter by dates, price, amenities, RV type, or delivery availability to find the right fit for your trip."
        },
        {
          "title": "2. Choose pickup or delivery",
          "text": "Pick up your RV from a local host {locationTitle} or arrange delivery directly to your campsite, home, or other approved destination."
        },
        {
          "title": "3. Get ready to travel",
          "text": "Confirm your booking and coordinate details with your host before departure. Insurance coverage and roadside assistance are included, so you can travel with confidence."
        }
      ],
      "exploreRentalRVs": "Explore rental RVs {locationTitle}"
    },
    "fr": {
      "heading": "Location d’{rvTitle} {locationTitle} : Comment ça marche",
      "content": "Planifier un voyage en VR {locationTitle} est simple avec RVezy. Recherchez les VRs disponibles, comparez les options, choisissez la collecte ou la livraison, et réservez en ligne de manière sécurisée. Que vous soyez en pleine nature, dans un parc national ou pour un week-end, votre aventure commence ici.",
      "campgroundDelivery": "un camping | un camping | n'importe quel de {n} campings",
      "destinationRvCount": "VRs à louer | 1 VR à louer | {n} VRs à louer",
      "destination": "{rvCountLabel} {locationTitleShort}, à partir de {minPrice} la nuit. Choisissez un VR pour vous-même, ou laissez le propriétaire le livrer et le mettre en place à {campgroundDelivery} {locationNearby}. Roulottes, motorisés et campervans, tous assurés, la plupart sont adaptés aux animaux de compagnie.",
      "steps": [
        {
          "title": "1. Explorez les VRs disponibles",
          "text": "Parcourez une large gamme de campervans, de roulottes et de motorisés. Filtrez par dates, prix, aménagements, type de VR ou disponibilité de livraison pour trouver le bon modèle pour votre voyage."
        },
        {
          "title": "2. Choisissez la collecte ou la livraison",
          "text": "Collectez votre VR auprès d'un hôte {locationTitle} ou arrangez la livraison directement à votre site de camping, votre domicile ou d'autres destinations approuvées."
        },
        {
          "title": "3. Préparez-vous à partir",
          "text": "Confirmez votre réservation et coordonnez les détails avec votre hôte avant le départ. La couverture d'assurance et l'assistance routière sont inclues, vous pouvez partir avec confiance."
        }
      ],
      "exploreRentalRVs": "Trouver des VRs {locationTitle}"
    }
  }
</i18n>
